import React, { useState } from 'react';
import { Alert, IconButton, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSortableReorderSensors } from '@/shared/hooks/useSortableReorderSensors';
import { reorderModifiers } from '@/shared/utils/dndModifiers';
import { getReorderPlacement, type ReorderPlacement } from '@/shared/utils/reorder';

/** Props handed to `renderItem` for each row. */
export interface SortableListRenderProps {
  /** Attach to the row's root element so dnd-kit can track and translate it. */
  setNodeRef: (element: HTMLElement | null) => void;
  /** Apply to the row's root element (drag transform + dragging opacity). */
  style: React.CSSProperties;
  /** Whether reordering is currently possible for this surface. */
  canDrag: boolean;
  /** Whether row controls should be disabled (external update or in-flight reorder). */
  controlsDisabled: boolean;
  /** Current item index in the provided sorted item array. */
  index: number;
  /** Pre-built left-side drag handle (null when `canDrag` is false). Place it in your layout. */
  dragHandle: React.ReactNode;
}

interface SortableListProps<T extends { id: string }> {
  items: readonly T[];
  canEdit: boolean;
  /** External loading flag (e.g. a save in flight) that should disable row controls. */
  isUpdating?: boolean;
  onMove: (itemId: string, placement: ReorderPlacement) => Promise<void>;
  renderItem: (item: T, props: SortableListRenderProps) => React.ReactNode;
  /** Wrap the rendered rows (e.g. in a MUI `<List>`). Defaults to a fragment. */
  renderContainer?: (children: React.ReactNode) => React.ReactNode;
  /** aria-label for each row's drag handle. */
  getItemLabel?: (item: T, index: number) => string;
  /** Message shown if a reorder fails. */
  reorderErrorMessage?: string;
}

interface SortableRowProps<T extends { id: string }> {
  item: T;
  index: number;
  canDrag: boolean;
  controlsDisabled: boolean;
  label: string;
  renderItem: (item: T, props: SortableListRenderProps) => React.ReactNode;
}

function SortableRow<T extends { id: string }>({
  item,
  index,
  canDrag,
  controlsDisabled,
  label,
  renderItem,
}: SortableRowProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: !canDrag,
  });

  const style: React.CSSProperties = {
    // Translate, not Transform: dnd-kit's post-drop FLIP transform carries scaleX/scaleY
    // that visibly squash rows whose measured box changed during the drag.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragHandle = canDrag ? (
    <Tooltip title="Reorder">
      <span>
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          disabled={controlsDisabled}
          aria-label={label}
          onClick={(event) => event.stopPropagation()}
          sx={{
            color: 'text.disabled',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  return (
    <>{renderItem(item, { setNodeRef, style, canDrag, controlsDisabled, index, dragHandle })}</>
  );
}

/**
 * Shared sortable list primitive. Owns the dnd-kit `DndContext`/`SortableContext`,
 * keyboard-capable sensors, the vertical-axis modifier, the drag-handle markup, the
 * reorder-error alert, and `canDrag`/`controlsDisabled` gating. Each surface supplies
 * its own row markup via `renderItem`, placing the provided `dragHandle` where it likes.
 *
 * Persistence/optimism is the caller's responsibility: `onMove` should call an RTK
 * mutation whose `onQueryStarted` applies the optimistic update with rollback.
 */
export function SortableList<T extends { id: string }>({
  items,
  canEdit,
  isUpdating = false,
  onMove,
  renderItem,
  renderContainer,
  getItemLabel,
  reorderErrorMessage = 'Failed to reorder. Please try again.',
}: SortableListProps<T>) {
  const sensors = useSortableReorderSensors();
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const canDrag = canEdit && items.length > 1;
  const controlsDisabled = isUpdating || isReordering;

  const moveItem = async (activeId: string, overId: string) => {
    const placement = getReorderPlacement(items, activeId, overId);
    if (!placement) return;

    try {
      setReorderError(null);
      setIsReordering(true);
      await onMove(activeId, placement);
    } catch (error) {
      console.error('Failed to reorder item:', error);
      setReorderError(reorderErrorMessage);
    } finally {
      setIsReordering(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (typeof active.id !== 'string' || typeof over.id !== 'string') return;
    void moveItem(active.id, over.id);
  };

  const rows = items.map((item, index) => (
    <SortableRow
      key={item.id}
      item={item}
      index={index}
      canDrag={canDrag}
      controlsDisabled={controlsDisabled}
      label={getItemLabel ? getItemLabel(item, index) : 'Reorder item'}
      renderItem={renderItem}
    />
  ));

  return (
    <>
      {reorderError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReorderError(null)}>
          {reorderError}
        </Alert>
      )}
      <DndContext
        sensors={sensors}
        modifiers={reorderModifiers}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {renderContainer ? renderContainer(rows) : rows}
        </SortableContext>
      </DndContext>
    </>
  );
}

export default SortableList;
